import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Flame, Leaf, Truck, Heart, Star, HeartHandshake, ArrowRight, Sparkles, Users, Package } from 'lucide-react';
import { Button } from '../components/ui/button';
import api from '../api/api';
import SEOMeta from '../components/SEOMeta';

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.15 },
  transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] }
};

const springUp = {
  initial: { opacity: 0, y: 30, scale: 0.97 },
  whileInView: { opacity: 1, y: 0, scale: 1 },
  viewport: { once: true, amount: 0.1 },
  transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] }
};

const iconMap = { Flame, Leaf, Truck, Heart, Star, HeartHandshake };

// Orbi fluttuanti nel hero (atmosfera foresta)
const HERO_ORBS = [
  { left: '-4%', top: '8%',  size: 380, dur: '10s', del: '0s',  color: 'var(--color-sage-soft)',      opacity: 0.75 },
  { left: '72%', top: '3%',  size: 280, dur: '13s', del: '2.5s', color: 'var(--color-terracotta-soft)', opacity: 0.5  },
  { left: '58%', top: '58%', size: 320, dur: '9s',  del: '1s',  color: 'var(--color-sage-soft)',      opacity: 0.6  },
  { left: '22%', top: '62%', size: 220, dur: '12s', del: '3s',  color: 'var(--color-linen)',           opacity: 0.8  },
  { left: '88%', top: '50%', size: 200, dur: '11s', del: '0.8s', color: 'var(--color-sage-soft)',      opacity: 0.45 },
];

// Stelle nel blocco scuro del Giardino (homepage)
const HOME_STARS = [
  {l:'6%', t:'14%',s:2,d:3.2,dl:0  },{l:'14%',t:'62%',s:1,d:4.5,dl:0.8},
  {l:'21%',t:'30%',s:2,d:3.8,dl:1.5},{l:'28%',t:'82%',s:1,d:5.0,dl:0.3},
  {l:'35%',t:'18%',s:3,d:3.5,dl:2.1},{l:'42%',t:'55%',s:1,d:4.2,dl:0.6},
  {l:'49%',t:'8%', s:2,d:3.9,dl:1.3},{l:'55%',t:'73%',s:2,d:4.8,dl:0.1},
  {l:'62%',t:'40%',s:1,d:3.3,dl:1.9},{l:'69%',t:'88%',s:3,d:4.1,dl:2.4},
  {l:'76%',t:'22%',s:2,d:3.6,dl:0.7},{l:'83%',t:'50%',s:1,d:5.0,dl:1.1},
  {l:'90%',t:'10%',s:2,d:4.4,dl:0.4},{l:'96%',t:'75%',s:1,d:3.7,dl:2.2},
  {l:'3%', t:'90%',s:2,d:4.6,dl:0.9},{l:'47%',t:'95%',s:1,d:3.1,dl:1.7},
];

export default function HomePage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const lang = i18n.language;

  const [services, setServices] = useState([]);
  const [products, setProducts] = useState([]);
  const [memorials, setMemorials] = useState([]);
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    api.get('/services').then(r => setServices(r.data.slice(0, 6))).catch(() => {});
    api.get('/products').then(r => setProducts(r.data.items?.slice(0, 4) || [])).catch(() => {});
    api.get('/memorials').then(r => setMemorials(r.data.items?.slice(0, 3) || [])).catch(() => {});
    api.get('/community/posts').then(r => setPosts(r.data.items?.slice(0, 2) || [])).catch(() => {});
  }, []);

  const speciesEmoji = { cane: '🐕', gatto: '🐈', coniglio: '🐇', uccello: '🐦', altro: '🐾' };

  return (
    <div className="bg-[var(--color-cream)]">
      <SEOMeta
        title="Home"
        description="Una Zampa in Paradiso — Cremazione individuale, urne artigianali, gioielli memoriali e Il Giardino delle Stelle: il cimitero digitale dove il ricordo di chi hai amato vive per sempre."
        url="/"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          "name": "Una Zampa in Paradiso",
          "description": "Servizi di cremazione per animali domestici, urne, gioielli memoriali e cimitero online.",
          "url": "https://unazampainparadiso.com",
          "image": "https://unazampainparadiso.com/logo.png",
          "priceRange": "€€",
          "areaServed": "Italy",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "Via Mangiacane, 11",
            "postalCode": "12011",
            "addressLocality": "Borgo San Dalmazzo",
            "addressRegion": "CN",
            "addressCountry": "IT"
          },
          "vatID": "04183140047",
          "legalName": "Una Zampa In Paradiso S.r.l.s."
        }}
      />
      {/* HERO */}
      <section className="hero-gradient min-h-screen lg:min-h-[90vh] flex items-center relative overflow-hidden" data-testid="hero-section">
        {/* Orbi atmosferici fluttuanti */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
          {HERO_ORBS.map((o, i) => (
            <div
              key={i}
              className="orb-float"
              style={{
                left: o.left, top: o.top,
                width: o.size, height: o.size,
                background: o.color,
                opacity: o.opacity,
                '--dur': o.dur, '--del': o.del,
              }}
            />
          ))}
        </div>
        <div className="container-custom py-10 lg:py-24 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            {/* Testo */}
            <motion.div {...fadeUp} className="max-w-xl">
              <div className="inline-flex items-center gap-2 bg-[var(--color-sage-soft)] text-[var(--color-sage)] text-xs font-semibold uppercase tracking-widest px-4 py-2 rounded-full mb-6">
                <Heart size={12} fill="currentColor" /> {t('hero.badge')}
              </div>
              <h1 className="font-serif text-4xl sm:text-5xl lg:text-7xl font-semibold text-[var(--color-deep)] leading-tight mb-6">
                {t('hero.title')}
              </h1>
              <p className="text-base lg:text-lg text-[var(--color-muted-text)] leading-relaxed mb-8">
                {t('hero.subtitle')}
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  size="lg"
                  className="bg-[var(--color-sage)] hover:bg-[var(--color-sage-dark)] text-white rounded-xl px-8 py-6 text-base font-medium btn-press shadow-lg"
                  onClick={() => navigate('/servizi')}
                  data-testid="hero-primary-cta"
                >
                  {t('hero.cta_services')} <ArrowRight size={18} className="ml-2" />
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="border-2 border-[var(--color-terracotta)] text-[var(--color-terracotta)] hover:bg-[var(--color-terracotta-soft)] rounded-xl px-8 py-6 text-base font-medium btn-press"
                  onClick={() => navigate('/giardino-delle-stelle')}
                  data-testid="hero-secondary-cta"
                >
                  <Star size={18} className="mr-2" /> {t('hero.cta_memorial')}
                </Button>
              </div>
            </motion.div>

            {/* Immagine — visibile su mobile in formato compatto, full su desktop */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
              className="relative mt-6 lg:mt-0"
            >
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <img
                  src="https://images.unsplash.com/photo-1450778869180-41d0601e046e?w=700&q=80"
                  alt="Animale con il suo padrone"
                  className="w-full h-56 sm:h-72 lg:h-[480px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[var(--color-deep)]/30 to-transparent" />
              </div>
              {/* Floating stats — solo desktop */}
              <div className="hidden lg:flex absolute -bottom-6 -left-6 bg-[var(--color-paper)] rounded-2xl shadow-xl p-4 items-center gap-3">
                <div className="w-12 h-12 bg-[var(--color-sage-soft)] rounded-xl flex items-center justify-center">
                  <Heart size={22} className="text-[var(--color-sage)]" fill="currentColor" />
                </div>
                <div>
                  <div className="font-serif font-semibold text-[var(--color-deep)] text-lg">2000+</div>
                  <div className="text-xs text-[var(--color-muted-text)]">Famiglie accompagnate</div>
                </div>
              </div>
              <div className="hidden lg:flex absolute -top-6 -right-6 bg-[var(--color-paper)] rounded-2xl shadow-xl p-4 items-center gap-3">
                <div className="w-12 h-12 bg-[var(--color-terracotta-soft)] rounded-xl flex items-center justify-center">
                  <Star size={22} className="text-[var(--color-terracotta)]" fill="currentColor" />
                </div>
                <div>
                  <div className="font-serif font-semibold text-[var(--color-deep)] text-lg">H24</div>
                  <div className="text-xs text-[var(--color-muted-text)]">Sempre disponibili</div>
                </div>
              </div>
              {/* Stats inline su mobile */}
              <div className="flex lg:hidden items-center justify-around mt-4 bg-[var(--color-paper)] rounded-2xl shadow p-3">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-[var(--color-sage-soft)] rounded-lg flex items-center justify-center">
                    <Heart size={14} className="text-[var(--color-sage)]" fill="currentColor" />
                  </div>
                  <div>
                    <div className="font-serif font-semibold text-[var(--color-deep)] text-sm">2000+</div>
                    <div className="text-[10px] text-[var(--color-muted-text)]">Famiglie</div>
                  </div>
                </div>
                <div className="w-px h-8 bg-[var(--color-border)]" />
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-[var(--color-terracotta-soft)] rounded-lg flex items-center justify-center">
                    <Star size={14} className="text-[var(--color-terracotta)]" fill="currentColor" />
                  </div>
                  <div>
                    <div className="font-serif font-semibold text-[var(--color-deep)] text-sm">H24</div>
                    <div className="text-[10px] text-[var(--color-muted-text)]">Disponibili</div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="section-py bg-[var(--color-paper)]" data-testid="services-section">
        <div className="container-custom">
          <motion.div {...fadeUp} className="text-center mb-12">
            <div className="inline-flex items-center gap-2 text-[var(--color-sage)] text-xs font-bold uppercase tracking-widest mb-3">
              <Flame size={14} /> I nostri servizi
            </div>
            <h2 className="font-serif text-4xl font-semibold text-[var(--color-deep)]">{t('services.title')}</h2>
            <p className="text-[var(--color-muted-text)] mt-3 max-w-xl mx-auto">{t('services.subtitle')}</p>
          </motion.div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((s, i) => {
              const Icon = iconMap[s.icon] || Heart;
              return (
                <motion.div
                  key={s.id}
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.08, duration: 0.5 }}
                >
                  <Link to={`/servizi`} className="block card-hover bg-[var(--color-paper)] border border-[var(--color-border)] rounded-2xl p-6 group shadow-sm" data-testid={`service-card-${s.id}`}>
                    <div className="w-12 h-12 bg-[var(--color-sage-soft)] rounded-xl flex items-center justify-center mb-4 group-hover:bg-[var(--color-sage)] transition-colors duration-200">
                      <Icon size={22} className="text-[var(--color-sage)] group-hover:text-white transition-colors duration-200" />
                    </div>
                    <h3 className="font-serif font-semibold text-[var(--color-deep)] text-lg mb-2">
                      {lang === 'en' ? s.name_en : s.name_it}
                    </h3>
                    <p className="text-sm text-[var(--color-muted-text)] line-clamp-2 mb-4">
                      {lang === 'en' ? s.description_en : s.description_it}
                    </p>
                    {s.price_info && (
                      <div className="text-xs font-medium text-[var(--color-sage)] bg-[var(--color-sage-soft)] px-3 py-1 rounded-full inline-block">
                        {s.price_info}
                      </div>
                    )}
                  </Link>
                </motion.div>
              );
            })}
          </div>
          <motion.div {...fadeUp} className="text-center mt-10">
            <Button variant="outline" className="border-[var(--color-sage)] text-[var(--color-sage)] hover:bg-[var(--color-sage-soft)] rounded-xl px-8" onClick={() => navigate('/servizi')}>
              {t('services.all')} <ArrowRight size={16} className="ml-2" />
            </Button>
          </motion.div>
        </div>
      </section>

      {/* SHOP PREVIEW */}
      <section className="section-py" data-testid="shop-preview-section">
        <div className="container-custom">
          <motion.div {...fadeUp} className="flex items-end justify-between mb-10">
            <div>
              <div className="inline-flex items-center gap-2 text-[var(--color-terracotta)] text-xs font-bold uppercase tracking-widest mb-3">
                <Package size={14} /> Shop memoriali
              </div>
              <h2 className="font-serif text-4xl font-semibold text-[var(--color-deep)]">{t('shop.title')}</h2>
            </div>
            <Button variant="ghost" className="hidden sm:flex text-[var(--color-sage)] hover:bg-[var(--color-sage-soft)]" onClick={() => navigate('/shop')}>
              {t('common.view_all')} <ArrowRight size={16} className="ml-1" />
            </Button>
          </motion.div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            {products.map((p, i) => (
              <motion.div
                key={p.id}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08, duration: 0.5 }}
                className="card-hover bg-[var(--color-paper)] border border-[var(--color-border)] rounded-2xl overflow-hidden shadow-sm cursor-pointer"
                onClick={() => navigate('/shop')}
                data-testid={`product-card-${p.id}`}
              >
                <div className="h-40 sm:h-48 overflow-hidden bg-[var(--color-sage-soft)] flex items-center justify-center p-4">
                  <img src="/logo.png" alt={lang === 'en' ? p.name_en : p.name_it} className="w-full max-w-[200px] h-auto object-contain rounded-xl opacity-85" />
                </div>
                <div className="p-4">
                  <span className="text-xs badge-cream px-2 py-0.5 rounded-full capitalize">{p.category}</span>
                  <h4 className="font-serif font-medium text-[var(--color-deep)] text-sm mt-2 line-clamp-2">
                    {lang === 'en' ? p.name_en : p.name_it}
                  </h4>
                  {p.price_info && <p className="text-xs text-[var(--color-sage)] font-medium mt-1">{p.price_info}</p>}
                </div>
              </motion.div>
            ))}
          </div>
          <div className="text-center mt-8 sm:hidden">
            <Button variant="outline" className="border-[var(--color-border)] rounded-xl" onClick={() => navigate('/shop')}>
              {t('common.view_all')} <ArrowRight size={16} className="ml-2" />
            </Button>
          </div>
        </div>
      </section>

      {/* IL GIARDINO DELLE STELLE */}
      <section className="section-py relative overflow-hidden" data-testid="garden-preview-section">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url('https://images.unsplash.com/photo-1713383658613-bdfad70a08d0?w=1400&q=80')` }}
        />
        {/* Overlay scuro profondo */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#060f09]/80 via-[var(--color-deep)]/75 to-[#060f09]/85" />
        {/* Stelle twinkling */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
          {HOME_STARS.map((s, i) => (
            <div
              key={i}
              className="star-pt"
              style={{ left: s.l, top: s.t, width: s.s + 'px', height: s.s + 'px', '--dur': s.d + 's', '--del': s.dl + 's' }}
            />
          ))}
          {/* Stelle cadenti */}
          <div className="shooting-star" style={{ left: '20%', top: '25%', '--dur': '2.0s', '--del': '5s'  }} />
          <div className="shooting-star" style={{ left: '60%', top: '8%',  '--dur': '1.9s', '--del': '11s' }} />
        </div>
        <div className="relative container-custom">
          <motion.div {...fadeUp} className="text-center mb-10">
            <div className="inline-flex items-center gap-2 text-[var(--color-terracotta-soft)] text-xs font-bold uppercase tracking-widest mb-3">
              <Star size={14} fill="currentColor" /> Cimitero online
            </div>
            <h2 className="font-serif text-4xl font-semibold text-white">{t('garden.title')}</h2>
            <p className="text-[var(--color-stone)] mt-3 max-w-xl mx-auto">{t('garden.subtitle')}</p>
          </motion.div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
            {memorials.map((m, i) => (
              <motion.div
                key={m.id}
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
              >
                <Link to={`/giardino-delle-stelle/${m.id}`} className="block glass-card-warm rounded-2xl overflow-hidden hover:bg-white/15 transition-all duration-300 shadow-xl group" data-testid={`memorial-card-${m.id}`}>
                  <div className="h-40 overflow-hidden">
                    <img src={m.photo_url || '/placeholder-pet.jpg'} alt={m.pet_name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                  </div>
                  <div className="p-4">
                    <div className="flex items-center justify-between mb-1">
                      <h4 className="font-serif font-semibold text-white">{m.pet_name}</h4>
                      <span className="text-sm">{speciesEmoji[m.species] || '🐾'}</span>
                    </div>
                    <p className="text-xs text-white/60">{m.breed}</p>
                    <div className="flex items-center gap-1 mt-2 text-xs text-white/60">
                      <span className="text-amber-400">♥</span>
                      <span className="font-medium text-white/90">{m.candles}</span>
                      <span>{t('garden.candles')}</span>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
          <motion.div {...fadeUp} className="text-center">
            <Button
              size="lg"
              className="bg-[var(--color-terracotta)] hover:bg-[var(--color-terracotta-dark)] text-white rounded-xl px-10 py-6 btn-press shadow-lg"
              onClick={() => navigate('/giardino-delle-stelle')}
              data-testid="garden-cta-button"
            >
              <Star size={18} className="mr-2" /> Visita il Giardino delle Stelle
            </Button>
          </motion.div>
        </div>
      </section>

      {/* COMMUNITY */}
      <section className="section-py bg-[var(--color-paper)]" data-testid="community-preview-section">
        <div className="container-custom">
          <motion.div {...fadeUp} className="flex items-end justify-between mb-10">
            <div>
              <div className="inline-flex items-center gap-2 text-[var(--color-sage)] text-xs font-bold uppercase tracking-widest mb-3">
                <Users size={14} /> Community
              </div>
              <h2 className="font-serif text-4xl font-semibold text-[var(--color-deep)]">{t('community.title')}</h2>
            </div>
            <Button variant="ghost" className="hidden sm:flex text-[var(--color-sage)] hover:bg-[var(--color-sage-soft)]" onClick={() => navigate('/community')}>
              {t('common.view_all')} <ArrowRight size={16} className="ml-1" />
            </Button>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {posts.map((p, i) => (
              <motion.div
                key={p.id}
                initial={{ opacity: 0, x: i % 2 === 0 ? -14 : 14 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
              >
                <Link to={`/community`} className="card-hover block bg-[var(--color-cream)] border border-[var(--color-border)] rounded-2xl overflow-hidden shadow-sm group">
                  {p.photo_url && (
                    <div className="h-48 overflow-hidden">
                      <img src={p.photo_url} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                    </div>
                  )}
                  <div className="p-6">
                    <div className="flex flex-wrap gap-2 mb-3">
                      {p.tags?.slice(0, 3).map(tag => (
                        <span key={tag} className="text-xs badge-sage px-2 py-0.5 rounded-full">#{tag}</span>
                      ))}
                    </div>
                    <h4 className="font-serif font-semibold text-[var(--color-deep)] text-lg mb-2 line-clamp-2">{p.title}</h4>
                    <p className="text-sm text-[var(--color-muted-text)] line-clamp-3">{p.content}</p>
                    <div className="flex items-center gap-4 mt-4 text-xs text-[var(--color-muted-text)]">
                      <span className="font-medium text-[var(--color-deep)]">{p.author_name}</span>
                      <span>♥ {p.likes}</span>
                      <span>💬 {p.comments?.length || 0}</span>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA CONTACT */}
      <section className="py-16 bg-[var(--color-deep)]" data-testid="contact-cta-section">
        <div className="container-custom text-center">
          <motion.div {...fadeUp}>
            <div className="inline-flex items-center gap-2 text-[var(--color-sage-soft)] text-xs font-bold uppercase tracking-widest mb-4">
              <Sparkles size={14} /> Siamo qui per te
            </div>
            <h2 className="font-serif text-4xl font-semibold text-white mb-4">
              Hai bisogno di assistenza?
            </h2>
            <p className="text-[var(--color-stone)] max-w-lg mx-auto mb-8">
              Il nostro team è disponibile 24 ore su 24, 7 giorni su 7. Non sei mai solo in questo momento difficile.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                className="bg-[var(--color-sage)] hover:bg-[var(--color-sage-dark)] text-white rounded-xl px-8 py-6 btn-press"
                onClick={() => navigate('/contatti')}
                data-testid="contact-cta-button"
              >
                Contattaci ora
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="border-[var(--color-stone)] text-[var(--color-linen)] hover:bg-white/10 rounded-xl px-8 py-6"
                onClick={() => navigate('/servizi')}
              >
                Scopri i servizi
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
